import { getEntriesByType, getEntry } from '@helpers/content/content';
import {
  craftMaxCraftableQuantity,
  craftMaxQueueableQuantity,
  requirementAvailable,
} from '@helpers/crafting/crafting-queue';
import { backfillReservedEquipment } from '@helpers/crafting/crafting-reserved-equipment';
import { recipeResultQuantity } from '@helpers/crafting/recipe-result';
import {
  isRecipeKnown,
  recipeBackdropSprite,
  recipeResultContent,
  recipeResultOwnedQuantity,
  recipeResultSpritesheet,
  recipesProducing,
} from '@helpers/crafting/recipes';
import {
  craftXpChance,
  craftXpChanceTier,
  tradeskillBuilding,
  tradeskillIdForName,
} from '@helpers/crafting/tradeskill';
import { isCollectibleDiscovered } from '@helpers/item/collectibles';
import { backfillEquipmentItem } from '@helpers/item/equipment';
import { isMaterialDiscovered } from '@helpers/item/materials';
import {
  isEquipmentDiscovered,
  pruneInvalidArmoryItems,
} from '@helpers/kingdom/armory';
import type {
  CollectibleContent,
  CraftRecipeEntry,
  CraftRequirementEntry,
  EquipmentContent,
  EquipmentItem,
  GameStateTradeskills,
  ItemContent,
  RecipeContent,
  RecipeRequirement,
  Tradeskill,
  TradeskillId,
} from '@interfaces';
import { RARITY_PRIORITY } from '@interfaces';
import { orderBy, sumBy } from 'es-toolkit/compat';

function isRequirementDiscovered(requirement: RecipeRequirement): boolean {
  if ('collectibleId' in requirement) {
    return isCollectibleDiscovered(requirement.collectibleId);
  }
  if ('equipmentId' in requirement) {
    return (
      isEquipmentDiscovered(requirement.equipmentId) ||
      recipesProducing(requirement.equipmentId).some(isRecipeKnown)
    );
  }
  return isMaterialDiscovered(requirement.itemId);
}

function buildRequirementEntry(
  requirement: RecipeRequirement,
): CraftRequirementEntry {
  if ('collectibleId' in requirement) {
    return {
      kind: 'collectible',
      content: getEntry<CollectibleContent>(requirement.collectibleId),
      spritesheet: 'collectible',
      quantity: 1,
      owned: isCollectibleDiscovered(requirement.collectibleId) ? 1 : 0,
    };
  }

  if ('equipmentId' in requirement) {
    return {
      kind: 'equipment',
      content: getEntry<EquipmentContent>(requirement.equipmentId),
      spritesheet: 'equipment',
      quantity: 1,
      owned: requirementAvailable(requirement),
    };
  }

  return {
    kind: 'item',
    content: getEntry<ItemContent>(requirement.itemId),
    spritesheet: 'item',
    quantity: requirement.quantity,
    owned: requirementAvailable(requirement),
  };
}

const REQUIREMENT_KIND_ORDER: Record<CraftRequirementEntry['kind'], number> = {
  collectible: 0,
  equipment: 1,
  item: 2,
};

// Collectible costs (not consumed) first, then equipment, then materials. Sorted before masking undiscovered content, so slots don't move once found.
function recipeRequirementEntries(
  recipe: RecipeContent,
): CraftRequirementEntry[] {
  const sorted = orderBy(
    recipe.requirements.map((requirement) => ({
      entry: buildRequirementEntry(requirement),
      discovered: isRequirementDiscovered(requirement),
    })),
    [
      ({ entry }) => REQUIREMENT_KIND_ORDER[entry.kind],
      ({ entry }) => entry.content?.name ?? '',
    ],
    ['asc', 'asc'],
  );

  return sorted.map(({ entry, discovered }) =>
    discovered ? entry : { ...entry, content: undefined },
  );
}

// Recipes the building has reached and that pass the craftability check are
// shown, plus dropped recipes still above the building's level (locked, at the bottom).
// Sorted by recipe level (not item level) so entries stay put as craftability changes.
export function getCraftableRecipeEntries(
  tradeskill: Tradeskill,
): CraftRecipeEntry[] {
  const building = tradeskillBuilding(tradeskill);
  const backdropSprite = recipeBackdropSprite();
  const tradeskillId = tradeskillIdForName(tradeskill);

  const entries: CraftRecipeEntry[] = getEntriesByType<RecipeContent>('recipe')
    .filter(
      (recipe) => recipe.tradeskillId === tradeskillId && isRecipeKnown(recipe),
    )
    .map((recipe) => {
      const levelLocked = building.level < recipe.minTradeskillLevel;
      const maxCraftable = levelLocked
        ? 0
        : craftMaxCraftableQuantity(recipe, tradeskill);
      const resultContent = recipeResultContent(recipe);
      const resultSpritesheet = recipeResultSpritesheet(recipe);

      return {
        recipe,
        resultContent,
        resultSpritesheet,
        resultChance: recipe.result.chance ?? 100,
        resultQuantity: recipeResultQuantity(recipe),
        backdropSprite,
        levelLocked,
        maxCraftable,
        maxQueueable: craftMaxQueueableQuantity(maxCraftable),
        ownedQuantity: recipeResultOwnedQuantity(recipe),
        xp: recipe.tradeskillXP,
        xpChance: craftXpChance(recipe, building.level),
        xpChanceTier: craftXpChanceTier(recipe, building.level),
        requirementEntries: recipeRequirementEntries(recipe),
      };
    });

  return orderBy(
    entries,
    [
      (entry) => entry.levelLocked,
      (entry) => entry.recipe.minTradeskillLevel,
      (entry) =>
        entry.resultContent ? RARITY_PRIORITY[entry.resultContent.rarity] : 0,
      (entry) => entry.recipe.name,
    ],
    ['asc', 'desc', 'asc', 'asc'],
  );
}

// Total ticks remaining across a whole queue - the active entry's remainder
// plus every not-yet-started unit (its own and every queued entry's).
export function craftQueueTicksRemaining(tradeskill: Tradeskill): number {
  return sumBy(tradeskillBuilding(tradeskill).queue, (entry) => {
    const recipe = getEntry<RecipeContent>(entry.recipeId);
    if (!recipe) return 0;

    const remainingUnits = entry.quantityTotal - entry.quantityCompleted;
    const remainingTicksThisUnit = recipe.craftTime - entry.ticksIntoCraft;

    return remainingTicksThisUnit + (remainingUnits - 1) * recipe.craftTime;
  });
}

// Gear held by queued crafts whose recipe no longer exists, so pruning those entries doesn't destroy it.
export function craftQueueOrphanedEquipment(
  tradeskills: GameStateTradeskills,
): EquipmentItem[] {
  const orphaned = Object.values(tradeskills).flatMap((building) =>
    building.queue
      .filter((entry) => !getEntry<RecipeContent>(entry.recipeId))
      .flatMap((entry) => entry.reservedEquipment ?? []),
  );
  return pruneInvalidArmoryItems(orphaned.map(backfillEquipmentItem));
}

// Drops any queued crafts whose recipeId no longer resolves to real content
// - e.g. after a recipe is renamed/removed from gamedata.
export function pruneInvalidCraftQueues(
  tradeskills: GameStateTradeskills,
): GameStateTradeskills {
  const pruned = { ...tradeskills };

  (Object.keys(pruned) as TradeskillId[]).forEach((tradeskillId) => {
    const building = pruned[tradeskillId];
    pruned[tradeskillId] = {
      ...building,
      queue: building.queue.flatMap((entry) => {
        const recipe = getEntry<RecipeContent>(entry.recipeId);
        if (!recipe) return [];

        const reserved = backfillReservedEquipment(entry, recipe);
        return [
          {
            ...entry,
            reservedEquipment: pruneInvalidArmoryItems(
              reserved.map(backfillEquipmentItem),
            ),
          },
        ];
      }),
    };
  });

  return pruned;
}
